import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
    this.setupPresenceRefreshListeners();
  }

  // Em aba de 2o plano o browser estrangula o setTimeout do ping de presenca,
  // entao a presenca no Redis expira (o agente vira "fantasma": online no painel,
  // offline para a distribuicao - ver issue #243). Pingamos na hora quando a aba
  // volta a ficar visivel / ganha foco / a rede volta, restabelecendo a presenca
  // sem esperar o proximo tick (que pode estar atrasado).
  setupPresenceRefreshListeners() {
    this.refreshPresence = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      )
        return;
      this.subscription.updatePresence();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.refreshPresence);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.refreshPresence);
      window.addEventListener('online', this.refreshPresence);
    }
  }

  removePresenceRefreshListeners() {
    if (!this.refreshPresence) return;

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.refreshPresence);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.refreshPresence);
      window.removeEventListener('online', this.refreshPresence);
    }
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.removePresenceRefreshListeners();
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
